import { useState } from "react";
import { Phone, MessageCircle, Clock, MapPin, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const WHATSAPP_NUMBER = "8898798857";
const PHONE_NUMBER = "+918898798857";

const services = [
  "Haircut - Men",
  "Haircut - Women",
  "Haircut - Kids",
  "Global Hair Colour",
  "Balayage",
  "Highlights",
  "Protein Treatment",
  "Hair Straightening",
  "Hair Perming",
  "Hair Extensions",
  "Facial",
  "Hydra-Facial",
  "Bridal Makeup",
  "Party Makeup",
  "Manicure",
  "Pedicure",
  "Fruit Waxing",
  "Other"
];

export default function BookAppointmentPage() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    service: "",
    date: "",
    time: ""
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleWhatsAppBooking = () => {
    let message = "Hi, I'd like to book an appointment at Bunty Hair Master.";
    
    if (formData.name || formData.service || formData.date || formData.time) {
      message = `Hi, I'd like to book an appointment at Bunty Hair Master.\n\n`;
      if (formData.name) message += `Name: ${formData.name}\n`;
      if (formData.phone) message += `Phone: ${formData.phone}\n`;
      if (formData.service) message += `Service: ${formData.service}\n`;
      if (formData.date) message += `Preferred Date: ${formData.date}\n`;
      if (formData.time) message += `Preferred Time: ${formData.time}`;
    }
    
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleDirectWhatsApp = () => {
    const message = encodeURIComponent("Hi, I'd like to book an appointment at Bunty Hair Master.");
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen pt-20" data-testid="book-appointment-page">
      {/* Hero Section */}
      <section className="section-spacing bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="font-['Cormorant_Garamond'] text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              Book Now
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white font-semibold leading-tight mb-6">
              Book Your Appointment
            </h1>
            <p className="text-white/70 text-lg">
              Ready for your transformation? Fill in your details below or contact us directly 
              via WhatsApp or phone.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section className="section-spacing bg-[#121212]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Form */}
            <div className="bg-[#0A0A0A] border border-white/5 p-8 md:p-10">
              <h2 className="font-['Playfair_Display'] text-2xl text-white mb-8">
                Quick Booking Form
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="form-label">Your Name</label>
                  <Input
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="input-underline"
                    data-testid="booking-name-input"
                  />
                </div>

                <div>
                  <label className="form-label">Phone Number</label>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="input-underline"
                    data-testid="booking-phone-input"
                  />
                </div>

                <div>
                  <label className="form-label">Select Service</label>
                  <Select 
                    value={formData.service} 
                    onValueChange={(value) => handleInputChange("service", value)}
                  >
                    <SelectTrigger 
                      className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-white focus:border-[#D4AF37] focus:ring-0"
                      data-testid="booking-service-select"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      {services.map((service) => (
                        <SelectItem 
                          key={service} 
                          value={service}
                          className="text-white hover:bg-[#D4AF37]/10 focus:bg-[#D4AF37]/10"
                        >
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Preferred Date</label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => handleInputChange("date", e.target.value)}
                      className="input-underline [color-scheme:dark]"
                      data-testid="booking-date-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Preferred Time</label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => handleInputChange("time", e.target.value)}
                      className="input-underline [color-scheme:dark]"
                      data-testid="booking-time-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleWhatsAppBooking}
                  className="w-full bg-[#25D366] hover:bg-[#1fb855] text-white font-medium py-4 rounded-none mt-4 flex items-center justify-center gap-2"
                  data-testid="booking-whatsapp-submit"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Book via WhatsApp
                </Button>
              </div>
            </div>

            {/* Contact Options */}
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl text-white mb-8">
                Or Contact Us Directly
              </h2>

              <div className="space-y-6">
                {/* WhatsApp */}
                <button
                  onClick={handleDirectWhatsApp}
                  className="w-full bg-[#25D366] p-6 flex items-center gap-4 hover:bg-[#1fb855] transition-colors"
                  data-testid="direct-whatsapp-btn"
                >
                  <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-white font-medium text-lg">WhatsApp Us</p>
                    <p className="text-white/70 text-sm">Quick response guaranteed</p>
                  </div>
                </button>

                {/* Call */}
                <a
                  href={`tel:${PHONE_NUMBER}`}
                  className="w-full bg-[#1A1A1A] border border-white/10 p-6 flex items-center gap-4 hover:border-[#D4AF37]/50 transition-colors block"
                  data-testid="direct-call-btn"
                >
                  <div className="w-14 h-14 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                    <Phone className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-medium text-lg">Call Us</p>
                    <p className="text-[#D4AF37]">+91 88987 98857</p>
                  </div>
                </a>

                {/* Info Cards */}
                <div className="grid grid-cols-2 gap-4 mt-8">
                  <div className="bg-[#1A1A1A] border border-white/5 p-6">
                    <Clock className="w-6 h-6 text-[#D4AF37] mb-3" />
                    <h4 className="text-white font-medium mb-1">Hours</h4>
                    <p className="text-white/60 text-sm">Open Daily<br />9:30 AM - 10:00 PM</p>
                  </div>
                  <div className="bg-[#1A1A1A] border border-white/5 p-6">
                    <MapPin className="w-6 h-6 text-[#D4AF37] mb-3" />
                    <h4 className="text-white font-medium mb-1">Location</h4>
                    <p className="text-white/60 text-sm">Vikas Palms,<br />Thane West</p>
                  </div>
                </div>

                {/* Confirmation Info */}
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 mt-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-white font-medium mb-2">What Happens Next?</h4>
                      <p className="text-white/60 text-sm">
                        After you reach out, our team will confirm your appointment 
                        via WhatsApp or call within a few minutes during business hours.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
